import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../../context/LanguageContext";
import { getTrustedAdult, saveTrustedAdult, getLinkedAdults } from "../../hooks/api";
import { CloseButton } from "../buttons/CloseButton";
import { SpeakButton } from "../common/SpeakButton";
import { AcceptButton } from "../buttons/AcceptButton";

export default function AdultosVinculadosModal({
  onClose,
  initialView = "list",
  fromEmotionSelector = false,
  targetEmotion = null,
}) {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const childId = localStorage.getItem("childId") || "";
  const ta = t("piggyBank.trustedAdult");

  const [view, setView] = useState(initialView);
  const [trustedAdult, setTrustedAdult] = useState({ nombre: "", mail: "" });
  const [linkedAdults, setLinkedAdults] = useState([]);
  const [loading, setLoading] = useState(true);

  const [nombre, setNombre] = useState("");
  const [mail, setMail] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [mailError, setMailError] = useState(false);

  const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value.trim());

  useEffect(() => {
    async function load() {
      try {
        const [taRes, laRes] = await Promise.all([
          getTrustedAdult(childId),
          getLinkedAdults(childId),
        ]);
        const existing = taRes.trustedAdult || { nombre: "", mail: "" };
        setTrustedAdult(existing);
        setNombre(existing.nombre || "");
        setMail(existing.mail || "");
        setLinkedAdults(laRes.adults || []);
      } catch {
        // silently ignore
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [childId]);

  const handleOpenForm = () => {
    setSaved(false);
    setView("form");
  };

  const handleSave = async () => {
    if (!nombre.trim() || !mail.trim()) return;
    if (!isValidEmail(mail)) { setMailError(true); return; }
    setMailError(false);
    setSaving(true);
    try {
      await saveTrustedAdult(childId, nombre.trim(), mail.trim());

      const updated = { nombre: nombre.trim(), mail: mail.trim() };
      setTrustedAdult(updated);

      const childUser = JSON.parse(localStorage.getItem("childUser") || "{}");
      childUser.adulto_de_confianza = updated;
      localStorage.setItem("childUser", JSON.stringify(childUser));

      setSaved(true);
      setTimeout(() => {
        onClose();
        if (fromEmotionSelector && targetEmotion) {
          navigate(`/detalle/${targetEmotion}`);
        } else {
          setView("list");
          setSaved(false);
        }
      }, 1200);
    } catch {
      // silently ignore
    } finally {
      setSaving(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 bg-black/50 flex items-end justify-center z-[9999] p-0 sm:items-center sm:p-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        <motion.div
          className="bg-white/95 backdrop-blur-sm w-full max-w-lg max-h-[85vh] overflow-y-auto rounded-t-3xl sm:rounded-3xl shadow-2xl"
          drag
          dragMomentum={false}
          dragElastic={0.1}
          initial={{ y: 80, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 80, opacity: 0 }}
          transition={{ type: "spring", damping: 28, stiffness: 300 }}
        >
          {/* Handle bar */}
          <div className="flex justify-center pt-3 pb-1">
            <div className="w-10 h-1 bg-purple-200 rounded-full" />
          </div>

          {/* Header */}
          <div className="flex items-center px-5 pt-2 pb-4 border-b border-gray-100">
            {view === "form" && (
              <button
                onClick={() => setView("list")}
                className="text-primary font-extrabold text-2xl pr-3 leading-none hover:opacity-70 transition-opacity"
              >
                ‹
              </button>
            )}
            <h2
              className="flex-1 text-lg font-extrabold text-primary"
              style={{ fontFamily: "'Fredoka One', cursive" }}
            >
              {view === "list" ? `👥 ${ta.menuItem}` : `🦋 ${ta.title}`}
            </h2>
            <CloseButton onClick={onClose} />
          </div>

          {/* Content */}
          <div className="px-5 pt-4 pb-6">
            {loading ? (
              <p className="text-center text-primary py-5">{ta.loading}</p>
            ) : view === "list" ? (
              <>
                <p className="text-[11px] font-bold text-gray-400 uppercase tracking-widest mb-2">
                  {ta.title}
                </p>

                <button
                  onClick={handleOpenForm}
                  className="flex items-center gap-3 w-full p-4 bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-100 rounded-2xl cursor-pointer text-left mb-5 hover:from-purple-100 hover:to-pink-100 transition-all"
                >
                  <span className="text-2xl">🦋</span>
                  <div className="flex-1">
                    <div className="font-bold text-gray-700 text-sm">
                      {trustedAdult.nombre || ta.noTrustedAdult}
                    </div>
                    {trustedAdult.mail && (
                      <div className="text-xs text-gray-400 mt-0.5">
                        {trustedAdult.mail}
                      </div>
                    )}
                  </div>
                  <span className="text-purple-300 text-lg">›</span>
                </button>

                <p className="text-[11px] font-bold text-gray-400 uppercase tracking-widest mb-2">
                  {ta.linkedTitle}
                </p>
                {linkedAdults.length === 0 ? (
                  <p className="text-gray-400 text-sm py-2">{ta.noLinkedAdults}</p>
                ) : (
                  <div className="border border-gray-100 rounded-2xl overflow-hidden">
                    {linkedAdults.map((adult, i) => (
                      <div
                        key={adult.adult_id}
                        className={`flex items-center gap-3 px-4 py-3 ${i < linkedAdults.length - 1 ? "border-b border-gray-100" : ""}`}
                      >
                        <span className="text-xl">👤</span>
                        <div>
                          <div className="font-semibold text-gray-700 text-sm">
                            {adult.nombre} {adult.apellido}
                          </div>
                          {adult.email && (
                            <div className="text-xs text-gray-400 mt-0.5">
                              {adult.email}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <>
                {/* Validation banner from EmotionSelector */}
                {fromEmotionSelector && (
                  <div className="bg-amber-50 border border-amber-400 rounded-2xl px-4 py-3 mb-4 text-sm text-amber-800 leading-relaxed flex items-center gap-2">
                    <span className="flex-1">⚠️ {ta.emotionValidation}</span>
                    <SpeakButton text={ta.emotionValidation} size="sm" />
                  </div>
                )}

                {/* Wobi message */}
                <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl p-4 mb-5 border-l-4 border-primary flex gap-3 items-start">
                  <span className="text-3xl flex-shrink-0">🦋</span>
                  <p className="text-sm leading-relaxed text-gray-700 flex-1">{ta.wobiMessage}</p>
                  <SpeakButton text={ta.wobiMessage} size="sm" className="flex-shrink-0 self-center ml-1" />
                </div>

                {/* Form */}
                <div className="flex flex-col gap-3">
                  <input
                    type="text"
                    placeholder={ta.namePlaceholder}
                    value={nombre}
                    onChange={(e) => setNombre(e.target.value)}
                    className="w-full px-4 py-3 rounded-2xl border border-purple-100 text-sm text-gray-700 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 bg-white transition-all"
                  />
                  <div>
                    <input
                      type="email"
                      placeholder={ta.emailPlaceholder}
                      value={mail}
                      onChange={(e) => {
                        setMail(e.target.value);
                        if (mailError && isValidEmail(e.target.value)) setMailError(false);
                      }}
                      className={`w-full px-4 py-3 rounded-2xl border text-sm text-gray-700 outline-none focus:ring-2 bg-white transition-all ${
                        mailError
                          ? "border-red-400 focus:border-red-400 focus:ring-red-200"
                          : "border-purple-100 focus:border-primary focus:ring-primary/20"
                      }`}
                    />
                    {mailError && (
                      <p className="text-red-500 text-xs mt-1 px-1">{ta.emailInvalid}</p>
                    )}
                  </div>

                  {saved && (
                    <p className="text-center text-emerald-600 font-bold text-sm">
                      ✓ {ta.saveSuccess}
                    </p>
                  )}

                  <div className="flex justify-center pt-1">
                    <AcceptButton
                      onClick={handleSave}
                      isLoading={saving}
                      disabled={saving || !nombre.trim() || !mail.trim() || mailError}
                      className="[&_img]:w-9 [&_img]:h-9"
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
